import React, { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Share2, TrendingUp, Sparkles, Monitor, Home, Camera, Palette, Smartphone } from 'lucide-react';
import { services, verticals, comingSoonVerticals } from '../data/mock';
import Layout from '../components/layout/Layout';

const iconMap = {
  Search: Search,
  Share2: Share2,
  TrendingUp: TrendingUp,
  Sparkles: Sparkles,
  Monitor: Monitor,
  Palette: Palette,
  Camera: Camera,
  Home: Home,
  Smartphone: Smartphone
};

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Services = () => {
  return (
    <Layout>
      {/* HERO SECTION */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-sunrise-hero relative overflow-hidden min-h-[60vh] flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-[10%] w-[400px] h-[400px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl">
            <TextReveal>
              <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-6">Our Expertise</span>
            </TextReveal>
            
            <TextReveal delay={0.1}>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#2D2D2D] leading-[1.05] mb-4">
                Everything you need.
              </h1>
            </TextReveal>
            <TextReveal delay={0.15}>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-8">
                <span className="text-gradient-sunrise">One collective.</span>
              </h1>
            </TextReveal>
            
            <TextReveal delay={0.2}>
              <p className="text-lg md:text-xl text-[#5A5A5A] max-w-2xl leading-relaxed mb-10">
                From building your brand to designing your space, capturing your story to creating your digital presence. 
                We bring together the right specialists for every project.
              </p>
            </TextReveal>

            {/* Vertical Tags */}
            <TextReveal delay={0.3}>
              <div className="flex flex-wrap gap-3">
                {verticals.map((vertical) => (
                  <span
                    key={vertical.slug}
                    className="px-4 py-2 rounded-full bg-white border border-[#FF6B35]/20 text-[#5A5A5A] text-sm hover:border-[#FF6B35] hover:text-[#FF6B35] transition-all cursor-default shadow-sm"
                  >
                    {vertical.name}
                  </span>
                ))}
              </div>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="space-y-4">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Search;
              
              return (
                <TextReveal key={service.id} delay={index * 0.05}>
                  <Link to={`/services/${service.slug}`}>
                    <motion.div
                      className="group relative p-8 md:p-10 rounded-2xl bg-[#FFFBF5] border border-[#FF6B35]/10 hover:border-[#FF6B35]/30 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-500 overflow-hidden cursor-pointer"
                      whileHover={{ y: -4 }}
                      data-testid={`service-item-${index}`}
                    >
                      {/* Bottom Line */}
                      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                      <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
                        {/* Number */}
                        <div className="flex-shrink-0">
                          <span className="font-display text-5xl md:text-6xl text-[#FF6B35]/10 group-hover:text-[#FF6B35]/20 transition-colors duration-500">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-[#FF6B35] text-xs uppercase tracking-widest font-medium">
                              {service.level}
                            </span>
                          </div>
                          <h3 className="text-2xl md:text-3xl font-display font-semibold text-[#2D2D2D] group-hover:text-[#FF6B35] transition-colors">
                            {service.title}
                          </h3>
                          <p className="mt-3 text-[#5A5A5A] max-w-2xl leading-relaxed">
                            {service.description}
                          </p>
                        </div>

                        {/* Features */}
                        <div className="flex-shrink-0 hidden lg:flex flex-wrap gap-2 max-w-xs">
                          {service.features?.slice(0, 3).map((feature, i) => (
                            <span key={i} className="px-3 py-1 rounded-full bg-white text-[#5A5A5A] text-xs border border-[#FF6B35]/10">
                              {feature}
                            </span>
                          ))}
                        </div>

                        {/* Arrow */}
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full border-2 border-[#FF6B35]/20 flex items-center justify-center group-hover:border-[#FF6B35] group-hover:bg-[#FF6B35] transition-all duration-300">
                            <ArrowRight size={20} className="text-[#FF6B35]/50 group-hover:text-white transition-colors" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </TextReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-padding bg-[#FFFBF5] relative overflow-hidden">
        <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] sun-orb sun-orb-soft opacity-30" />

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <TextReveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-4">Why Us</span>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2D2D]">
                The Collective <span className="text-gradient-sunrise">Advantage</span>
              </h2>
            </div>
          </TextReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: '⚡', title: 'Multi-Vertical Expertise', desc: 'One collective for all your needs: marketing, design, photography & more.' },
              { icon: '🎯', title: 'Domain Specialists', desc: 'Each vertical is handled by experts who live and breathe their craft.' },
              { icon: '🤝', title: 'Seamless Collaboration', desc: 'Our specialists work together to deliver cohesive, integrated solutions.' }
            ].map((item, index) => (
              <TextReveal key={item.title} delay={index * 0.1}>
                <motion.div
                  className="card-fresh text-center"
                  whileHover={{ y: -8 }}
                >
                  <span className="text-4xl mb-6 block">{item.icon}</span>
                  <h3 className="text-xl font-display font-semibold text-[#2D2D2D] mb-3">{item.title}</h3>
                  <p className="text-[#5A5A5A] leading-relaxed">{item.desc}</p>
                </motion.div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMING SOON */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-4">Expanding Soon</span>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2D2D]">
                New Verticals <span className="text-gradient-sunrise">Coming</span>
              </h2>
              <p className="mt-4 text-[#5A5A5A]">
                Our collective is growing. Stay tuned for these exciting new services.
              </p>
            </div>
          </TextReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {comingSoonVerticals.map((vertical, index) => (
              <TextReveal key={vertical.name} delay={index * 0.1}>
                <div className="relative p-6 rounded-2xl border-2 border-dashed border-[#FF6B35]/20 text-center bg-[#FFFBF5]">
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-xs font-semibold rounded-full">
                    {vertical.status}
                  </span>
                  <span className="text-3xl block mt-4 mb-4">
                    {vertical.icon === 'Mic' ? '🎙️' : vertical.icon === 'Wallet' ? '💰' : '🎬'}
                  </span>
                  <h3 className="text-lg font-semibold text-[#2D2D2D] mb-2">{vertical.name}</h3>
                  <p className="text-[#8A8A8A] text-sm">{vertical.description}</p>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="section-padding bg-gradient-to-br from-[#FF6B35] to-[#FFB627] relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <TextReveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white mb-4">
                Ready to grow with
              </h2>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white/90 mb-8">
                our collective?
              </h2>
              <p className="text-lg text-white/80 max-w-xl mx-auto mb-10">
                Whether you need marketing, design, photography, or tech solutions, 
                our specialists are ready to help you succeed.
              </p>
              <Link to="/contact">
                <motion.button
                  className="px-10 py-5 bg-white text-[#FF6B35] rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start a Conversation
                  <ArrowRight size={20} />
                </motion.button>
              </Link>
            </div>
          </TextReveal>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
